import React, { Component, useEffect, useLayoutEffect, useRef } from 'react';
import cx from 'classnames';
import { Logger } from '@elyx-code/common-ts-utils';
import './popup.css';
import { ICanvasElementComponentProps } from '../../services/editor/CanvasElement';

export interface ICanvasPopupBaseComponentProps
	extends Partial<ICanvasElementComponentProps> {
	containerClasses?: string[];
	arrowClasses?: string[];
	onClick?: (event: any) => any;
	id?: string;
	children: React.ReactNode;
}

// Define an error boundary class that will catch errors in the child component.
class ErrorBoundary extends Component<
	{ children: React.ReactNode },
	{ hasError: boolean }
> {
	constructor(props: { children: React.ReactNode }) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: any) {
		return { hasError: true };
	}

	componentDidCatch(error: any, errorInfo: any) {
		Logger.error('ErrorBoundary caught an error:', error, errorInfo);
	}

	render() {
		if (this.state.hasError) {
			// Optionally, you can render a fallback UI.
			return null;
		}
		return this.props.children;
	}
}

// The original functional component, renamed for clarity.
function CanvasPopupBaseComponentFunctional(
	props: ICanvasPopupBaseComponentProps
) {
	const rootElementRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const element = rootElementRef.current;
		if (element && document.contains(element)) {
			props.onMount?.(element);
		}

		// When the component is unmounted.
		return () => {
			props.onUnmount?.();
		};
	}, []);

	useEffect(() => {
		const element = rootElementRef.current;
		props.onUpdate?.(element);
	});

	useLayoutEffect(() => {
		props.onBeforeUpdate?.();
	}, []);

	const {
		containerClasses,
		id,
		onMount,
		onUnmount,
		onUpdate,
		onBeforeUpdate,
		onClick,
		...otherProps
	} = props;

	const stopPropagation = (e: React.SyntheticEvent, customHandler?: (e: any) => void) => {
		e.stopPropagation();
		if (e.nativeEvent) {
			e.nativeEvent.stopPropagation();
			e.nativeEvent.stopImmediatePropagation?.();
		}
		customHandler?.(e);
	};

	return (
		<div
			ref={rootElementRef}
			id={id}
			{...otherProps}
			className={cx(['popup', ...(containerClasses || [])])}
			style={{ display: 'revert', position: 'relative' }}
			onClick={(e) => stopPropagation(e, onClick)}
			onDoubleClick={(e) => stopPropagation(e, (otherProps as any)?.onDoubleClick)}
			onContextMenu={(e) => stopPropagation(e, (otherProps as any)?.onContextMenu)}
			onMouseDown={(e) => stopPropagation(e, (otherProps as any)?.onMouseDown)}
			onMouseUp={(e) => stopPropagation(e, (otherProps as any)?.onMouseUp)}
			onPointerDown={(e) => stopPropagation(e, (otherProps as any)?.onPointerDown)}
			onPointerUp={(e) => stopPropagation(e, (otherProps as any)?.onPointerUp)}
		>
			{props.children}
		</div>
	);
}

// Export the wrapped component so the API remains the same.
export default function CanvasPopupBaseComponent(
	props: ICanvasPopupBaseComponentProps
) {
	return (
		<ErrorBoundary>
			<CanvasPopupBaseComponentFunctional {...props} />
		</ErrorBoundary>
	);
}
